/**
 * Batch SKU Renamer, Marketplace Preset Resizer & ZIP Exporter
 * -------------------------------------------------------------
 * Pure logic only - no DOM APIs - so it is fully unit-testable:
 *   - marketplace dimension presets (Amazon / Etsy / Shopify)
 *   - SEO slug + sequential batch filename generation (`{sku}-main-01.jpg`)
 *   - freemium batch quota checks
 *   - client-side ZIP creation via `fflate`
 */

import { unzipSync, zipSync, strToU8 } from 'fflate';
import { batchLimitFor, FREE_BATCH_LIMIT, PRO_BATCH_LIMIT } from './license.ts';

export type MarketplaceId = 'amazon' | 'etsy' | 'shopify';
export type ExportFormat = 'image/jpeg' | 'image/webp';

export interface ExportPreset {
  platform: 'Amazon' | 'Etsy' | 'Shopify';
  width: number;
  height: number;
  format: ExportFormat;
}

export const MARKETPLACE_PRESETS: Record<MarketplaceId, ExportPreset> = {
  amazon: { platform: 'Amazon', width: 2000, height: 2000, format: 'image/jpeg' },
  etsy: { platform: 'Etsy', width: 2000, height: 1600, format: 'image/jpeg' },
  shopify: { platform: 'Shopify', width: 2048, height: 2048, format: 'image/webp' }
};

export const PRESET_ORDER: MarketplaceId[] = ['amazon', 'etsy', 'shopify'];

export interface QuotaResult {
  allowed: boolean;
  limit: number;
  message?: string;
}

/**
 * Freemium gate: free users may export up to 5 images per batch, Pro users up
 * to 1000 (the "100+ batch ZIP export" promise).
 */
export function checkBatchQuota(imageCount: number, isPro: boolean): QuotaResult {
  const limit = batchLimitFor(isPro);
  if (imageCount > limit) {
    return {
      allowed: false,
      limit,
      message: isPro
        ? `Pro batches are capped at ${limit.toLocaleString('en-US')} images per ZIP.`
        : `Free tier allows up to ${FREE_BATCH_LIMIT} images per batch. Upgrade to Pro for ${PRO_BATCH_LIMIT}+ batch ZIP export.`
    };
  }
  return { allowed: true, limit };
}

/** SEO slug: lower-case, alphanumeric + hyphens, never empty. */
export function slugifySku(raw: string): string {
  const slug = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
  return slug.length > 0 ? slug : 'product';
}

/**
 * Sequential batch filename: `{sku}-main-01.jpg`.
 * `index` is 1-based; numbers below 10 keep a leading zero.
 */
export function generateBatchFilename(sku: string, index: number, ext: string = 'jpg'): string {
  const cleanSku = slugifySku(sku);
  const safeExt = ext.replace(/^\./, '').toLowerCase() || 'jpg';
  const pad = String(Math.max(1, Math.trunc(index))).padStart(2, '0');
  return `${cleanSku}-main-${pad}.${safeExt}`;
}

/** File extension for an export format. */
export function extensionForFormat(format: ExportFormat): 'jpg' | 'webp' {
  return format === 'image/webp' ? 'webp' : 'jpg';
}

export interface ZipFileInput {
  /** Path inside the ZIP (folders allowed via `/`) */
  name: string;
  data: Uint8Array;
}

/** Builds an in-memory ZIP archive (stored, no compression - images are already compressed). */
export function createBatchZip(files: ZipFileInput[]): Uint8Array {
  if (files.length === 0) {
    throw new Error('createBatchZip: no files to archive');
  }
  const tree: Record<string, Uint8Array> = {};
  for (const file of files) {
    tree[file.name] = file.data;
  }
  return zipSync(tree, { level: 0, mtime: new Date('2024-01-01T00:00:00Z') });
}

/** Reads file names back out of a ZIP built by `createBatchZip` (used by tests & verification). */
export function listZipEntries(zip: Uint8Array): string[] {
  return Object.keys(unzipSync(zip)).sort();
}

/** ASCII-safe text helper for ZIP entries. */
export function textEntry(text: string): Uint8Array {
  return strToU8(text);
}
